import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * /hygiene-trilogy — Sales page for "The Hygiene Trilogy" ($11 Mini E-Book bundle)
 * Three companion mini e-books (Mind, Body, Spirit) to the "I GET TO" book.
 * Gumroad checkout: https://templefranklin22.gumroad.com/l/ygmyv
 */

const BRAND = {
  teal: "#068fa2",
  tealDeep: "#0a5b6a",
  deepTeal: "#042f2e",
  cream: "#efeae7",
  gold: "#d4af6a",
  ink: "#1e2a29",
  muted: "#5f7370",
};

const GUMROAD_URL = "https://templefranklin22.gumroad.com/l/ygmyv";
const COVER_IMAGE = "/downloads/mini-library-gumroad-cover-1280x720.png";
const THUMB_IMAGE = "/downloads/mini-library-gumroad-thumbnail-600x600.png";

const BOOKS = [
  {
    title: "Mind Hygiene",
    blurb: "For the noise in your head. Tools to notice your thoughts, name them, and choose what stays.",
    accent: "the thoughts you think on repeat",
  },
  {
    title: "Body Hygiene",
    blurb: "For the body you live in. Honest, soft tools — no diet talk, no shame — for coming back into your skin.",
    accent: "the body you've been ignoring",
  },
  {
    title: "Spirit Hygiene",
    blurb: "For the part of you that already knows. A little bit of Woo, a lot of loving you. Define your sacred for yourself.",
    accent: "the quiet knowing underneath",
  },
];

export default function HygieneTrilogyPage() {
  return (
    <div
      data-testid="hygiene-trilogy-page"
      style={{ backgroundColor: BRAND.cream, minHeight: "100vh", color: BRAND.ink }}
    >
      <Helmet>
        <meta name="keywords" content="Mind Body Spirit HYGIENE™, mini ebooks, Temple Franklin, I GET TO, mindfulness, self-care, midlife, divorce, grief, women, Hygiene Trilogy" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "The Hygiene Trilogy: Mind · Body · Spirit",
          "description": "Three soulful mini e-books — Mind Hygiene, Body Hygiene, Spirit Hygiene. Companions to the I GET TO book.",
          "brand": { "@type": "Brand", "name": "Mind Body Spirit HYGIENE\u2122" },
          "image": `https://mindbodyspirithygienetools.com${COVER_IMAGE}`,
          "author": { "@type": "Person", "name": "Temple Franklin" },
          "offers": {
            "@type": "Offer",
            "price": "11.00",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": GUMROAD_URL,
          },
        })}</script>
      </Helmet>

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="px-6 pt-14 pb-10 md:pt-20 md:pb-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Left — copy */}
          <div>
            <div
              data-testid="badge-new"
              className="inline-block uppercase tracking-[0.25em] text-xs px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: BRAND.teal, color: BRAND.cream }}
            >
              ✦ New · Instant Download
            </div>

            <p
              className="text-sm md:text-base italic mb-4"
              style={{ color: BRAND.deepTeal, fontFamily: "Georgia, 'Cormorant Garamond', serif", letterSpacing: "0.02em" }}
              data-testid="reframe-signature"
            >
              It&apos;s not a practice. It&apos;s hygiene.
            </p>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl mb-5"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif", color: BRAND.deepTeal, lineHeight: 1.05 }}
            >
              The{" "}
              <span style={{ fontStyle: "italic", color: BRAND.gold }}>
                Hygiene
              </span>{" "}
              Trilogy
            </h1>

            <p
              className="text-base md:text-lg mb-6 italic"
              style={{ color: BRAND.tealDeep, opacity: 0.9 }}
            >
              Mind · Body · Spirit — three mini e-books from Temple Franklin.
            </p>

            <p
              className="text-base md:text-lg leading-relaxed mb-8"
              style={{ color: BRAND.ink, opacity: 0.9 }}
            >
              Three soulful field guides — one for the noise in your head, one
              for the body you live in, one for the part of you that already
              knows. Companions to the <em>I GET TO</em> book, in pocket form.
            </p>

            <p
              className="text-4xl md:text-5xl mb-1"
              style={{
                color: BRAND.deepTeal,
                fontFamily: "Georgia, serif",
                fontWeight: 700,
              }}
              data-testid="price"
            >
              $11
            </p>
            <p className="text-sm italic mb-7" style={{ color: BRAND.teal, opacity: 0.9 }}>
              Three printable PDFs · instant download · use on phone or print
            </p>

            <a
              href={GUMROAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-now-btn-hero"
              className="inline-block px-10 py-4 rounded-md uppercase tracking-[0.2em] text-sm md:text-base transition-opacity hover:opacity-90"
              style={{
                backgroundColor: BRAND.deepTeal,
                color: BRAND.cream,
                fontWeight: 600,
              }}
            >
              Buy on Gumroad · $11 →
            </a>

            <p className="text-xs mt-4" style={{ color: BRAND.muted }}>
              Secure checkout via Gumroad · 30-day money-back guarantee
            </p>
          </div>

          {/* Right — cover image */}
          <div className="flex justify-center">
            <img
              src={COVER_IMAGE}
              alt="The Hygiene Trilogy — three deep teal mini e-books on cream linen"
              data-testid="cover-image"
              className="w-full max-w-xl rounded-lg shadow-2xl"
              style={{ border: `1px solid ${BRAND.gold}40` }}
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GET ─────────────────────────────── */}
      <section className="px-6 py-14 md:py-20" style={{ backgroundColor: "#fff" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p
              className="uppercase tracking-[0.3em] text-xs mb-2"
              style={{ color: BRAND.gold }}
            >
              ✦ What you get ✦
            </p>
            <h2
              className="text-3xl md:text-4xl"
              style={{ fontFamily: "Georgia, serif", color: BRAND.deepTeal }}
            >
              Three soulful mini e-books
            </h2>
            <p
              className="text-sm md:text-base mt-3 italic max-w-2xl mx-auto"
              style={{ opacity: 0.75, color: BRAND.ink }}
            >
              Not a repeat of the book. New material. Three short field guides
              you can print, mark up, and live with.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8" data-testid="books-grid">
            {BOOKS.map((book, i) => (
              <div
                key={book.title}
                data-testid={`book-card-${i}`}
                className="rounded-lg p-7 transition-transform hover:scale-[1.02]"
                style={{
                  backgroundColor: BRAND.cream,
                  border: `1px solid ${BRAND.gold}30`,
                }}
              >
                <p
                  className="uppercase tracking-[0.2em] text-xs mb-3"
                  style={{ color: BRAND.teal }}
                >
                  Book {i + 1}
                </p>
                <h3
                  className="text-2xl md:text-3xl mb-3"
                  style={{ fontFamily: "Georgia, serif", color: BRAND.deepTeal }}
                >
                  {book.title}
                </h3>
                <p
                  className="text-sm italic mb-4"
                  style={{ color: BRAND.gold, fontWeight: 600 }}
                >
                  For {book.accent}.
                </p>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: BRAND.ink, opacity: 0.85 }}
                >
                  {book.blurb}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ─────────────────────────────── */}
      <section
        className="px-6 py-14 md:py-20"
        style={{ backgroundColor: BRAND.cream }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ Who it&apos;s for ✦
          </p>
          <h2
            className="text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "Georgia, serif", color: BRAND.deepTeal }}
          >
            Women in the messy middle
          </h2>
          <p
            className="text-base md:text-lg leading-relaxed mb-4"
            style={{ color: BRAND.ink, opacity: 0.9 }}
          >
            The parts of the story nobody warned you about. Divorce. Grief.
            Estrangement. Midlife reckoning. The quiet collapse no one sees.
          </p>
          <p
            className="text-base md:text-lg leading-relaxed italic"
            style={{ color: BRAND.tealDeep }}
          >
            Caveman-simple tools you can use today. Build them in the calm,
            lovely, so you have them in the chaos.
          </p>
        </div>
      </section>

      {/* ── HOW TO READ ──────────────────────────────── */}
      <section className="px-6 py-14 md:py-20" style={{ backgroundColor: "#fff" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p
              className="uppercase tracking-[0.3em] text-xs mb-2"
              style={{ color: BRAND.gold }}
            >
              ✦ How to read me ✦
            </p>
            <h2
              className="text-3xl md:text-4xl"
              style={{ fontFamily: "Georgia, serif", color: BRAND.deepTeal }}
            >
              Caveman-simple. Read slow.
            </h2>
          </div>

          <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: BRAND.ink }}>
            <p>
              <span style={{ color: BRAND.teal, fontWeight: 600 }}>Print me.</span>{" "}
              These are made to be marked up, dog-eared, and carried. Not skimmed on a phone in line at the store.
            </p>
            <p>
              <span style={{ color: BRAND.teal, fontWeight: 600 }}>Read slow.</span>{" "}
              One section a sitting. Let the questions land before you answer them.
            </p>
            <p>
              <span style={{ color: BRAND.teal, fontWeight: 600 }}>Make them personal.</span>{" "}
              No dogma. No one else&apos;s answers. Your power clause: honor your own faith or path — keep what&apos;s yours, leave the rest.
            </p>
            <p>
              <span style={{ color: BRAND.teal, fontWeight: 600 }}>Information, not instruction.</span>{" "}
              Take what&apos;s yours, leave the rest.
            </p>
          </div>
        </div>
      </section>

      {/* ── AUTHOR NOTE ──────────────────────────────── */}
      <section
        className="px-6 py-14 md:py-20"
        style={{ backgroundColor: BRAND.deepTeal, color: BRAND.cream }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ A note from Temple
          </p>
          <h2
            className="text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "Georgia, serif" }}
          >
            &ldquo;I build what I teach. I walk the talk.&rdquo;
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-90 mb-6">
            I wrote the Trilogy because the <em>I GET TO</em> book holds the
            method, and these mini guides hold the everyday. They&apos;re the things
            I print and tape inside cabinet doors. The lines I read again when
            the chaos hits.
          </p>
          <p className="text-base md:text-lg leading-relaxed opacity-90 mb-6">
            I teach you these tools before sh!t hits the fan — so you have
            them in the chaos.
          </p>
          <p className="text-sm italic opacity-75">
            — Temple Franklin
            <br />
            Founder of the Mind Body Spirit HYGIENE™ Method
          </p>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────── */}
      <section
        className="px-6 py-16 md:py-24 text-center"
        style={{ backgroundColor: BRAND.cream }}
      >
        <div className="max-w-2xl mx-auto">
          <img
            src={THUMB_IMAGE}
            alt="The Hygiene Trilogy thumbnail"
            className="w-40 md:w-56 mx-auto mb-8 rounded-lg shadow-xl"
            style={{ border: `1px solid ${BRAND.gold}40` }}
            loading="lazy"
          />
          <h2
            className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "Georgia, serif", color: BRAND.deepTeal }}
          >
            Ready to come home?
          </h2>
          <p
            className="text-base md:text-lg mb-8 italic"
            style={{ color: BRAND.ink, opacity: 0.85 }}
          >
            Three mini e-books. One quiet ritual. $11.
          </p>
          <a
            href={GUMROAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="buy-now-btn-footer"
            className="inline-block px-12 py-4 rounded-md uppercase tracking-[0.2em] text-sm md:text-base transition-opacity hover:opacity-90"
            style={{
              backgroundColor: BRAND.deepTeal,
              color: BRAND.cream,
              fontWeight: 600,
            }}
          >
            Buy on Gumroad · $11 →
          </a>
          <p className="text-xs mt-4" style={{ color: BRAND.muted }}>
            Instant PDF download · 30-day money-back guarantee
          </p>

          <div className="mt-12 pt-8" style={{ borderTop: `1px solid ${BRAND.gold}30` }}>
            <p className="text-sm" style={{ color: BRAND.muted }}>
              Have the workbook already?{" "}
              <Link
                to="/book"
                className="underline"
                style={{ color: BRAND.teal, fontWeight: 600 }}
              >
                Read about the I GET TO book →
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
